import { Key, useEffect, useState } from "react";
import { Button, Flex, Pagination, Skeleton, Table, TableColumnsType } from "antd";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import dayjs from "dayjs";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { mdlStopFreeSaleReport } from "../../models/domain/StopFreeSaleReport";
import { mdlTablePagination } from "../../models/ui-models/TablePagination";
import { mdlStopFreeSaleReportRequest } from "../../models/service-models/stop-free-sale/StopFreeSaleReportRequest";
import { StopFreeSaleService } from "../../services/StopFreeSaleService";
import {  useQuery } from "react-query";
import { mdlDatePair } from "../../models/domain/DatePair";
import { ExpandableStopFreeReportList } from "./ExpandableStopFreeReportList";
import StopSaleReport from "./StopFreeSaleReportTableNew";
import { enmStopFreeSaleReportTypes } from "../../models/enums/StopFreeSaleReportTypes";


export type StopFreeSaleReportTableProps = {
  data:mdlStopFreeSaleReport[] | undefined;
  isLoading?: boolean;
  activeMonth?: number;
  activeYear?: number;
  hotelIds?: string[] | undefined;
  agencies?: string[] | undefined;
  markets?: string[] | undefined;
  rooms?: string[] | undefined;
  roomTypes?: string[] | undefined;
  boards?: string[] | undefined;
  reportTypes?: enmStopFreeSaleReportTypes[] | undefined;
  onTableParamsChange?: Function;
  onPaginationChange?: Function;
  pagination: mdlTablePagination | undefined;
  onRowSelection?: Function;
  selectedRowKeys?: number[] | undefined;
  tableTools?: JSX.Element;
  onEmailClick?: Function;
  onPrintClick?: Function;
  onPDFClick?: Function;
  selectedHotels?: string[] | undefined;
  formValues?: any;
  fnHandleActiveDay?: Function;
}



export const StopFreeSaleReportTable = (props: StopFreeSaleReportTableProps) => {
  const { t } = useTranslation();
  
  const [dateColList, setDateColList] = useState<mdlDatePair[]>();
  useEffect(() => {
    if(!dateColList){
      setSurroundingMonths(dayjs().toDate())
    }
  }, [props]);

  const setSurroundingMonths = (currentTime:Date) => {
    const currentMonth = dayjs(currentTime);
    const months = [];
    for (let i = 8; i > 0; i--) {
      months.push({
        monthText: currentMonth.subtract(i, 'month').format('MMMM'),
        month: currentMonth.subtract(i, 'month').format('MM'),
        year: currentMonth.subtract(i, 'month').format('YYYY')
      }as mdlDatePair);
    }
    months.push({
      month: currentMonth.format('MM'),
      monthText: currentMonth.format('MMMM'),
      year: currentMonth.format('YYYY')
    } as mdlDatePair);

    for (let i = 1; i <= 8; i++) {
      months.push({
        month: currentMonth.add(i, 'month').format('MM'),
        monthText: currentMonth.add(i, 'month').format('MMMM'),
        year: currentMonth.add(i, 'month').format('YYYY')
      } as mdlDatePair);
    }

    setDateColList(months);
  };

  const getDaysInMonth = (month: number, year: number) => {
    const days = [];
    const date = dayjs(`${year}-${month}-01`); 
    const daysInMonth = date.daysInMonth(); 

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDay = date.date(i);
      days.push({
        day: currentDay.format('DD'),    
        date: currentDay.format('YYYY-MM-DD'),    
        dateTime: currentDay,     
        dayName: currentDay.format('ddd'),       
      });
    }

    return days;
  };

  function getBaseColumns(): TableColumnsType<mdlStopFreeSaleReport> {
    let defaultColumns = [{
      title: t("stopFreeSale.report.hotel"),
      key: 'hotelName',
      width: 270,
      render: (e: mdlStopFreeSaleReport) => {
        return e.hotelNames?.join(",")
      }
    }] as any;
    let dayList = [] as any;
    if (props.activeMonth && props.activeYear) {
      dayList = getDaysInMonth(props.activeMonth, props.activeYear).map((x: any) => {
        return {
          title: <>
            <span className="text-center d-block">{x.dayName} <br /> {x.day}</span>
          </>,
          width:34,
          key: `day${x.day}`,
          render: (e: mdlStopFreeSaleReport) => {
            if (e.matrixList && e.matrixList.length >= x.day && e.matrixList[x.day] != null) {
              return e.matrixList[x.day].type == "stop" ? <span className="stop">stop</span> : <span className="free">free</span> 
            }
            return <></>
          }
        }
      })
    }
    return [...defaultColumns, ...dayList];
  }

  const getScreenSize = () => {
    return `${(window.screen.height * 65) / 100}px`;
  }

const handleActiveDay = (month?: number,year?:number) => {
  props.onRowSelection && props.onRowSelection([]);
  props.fnHandleActiveDay && props.fnHandleActiveDay(month,year)
};

const handleArrowClick = (direction: "Next" | "Prev") => {
  if(!props.activeMonth || !props.activeYear || !props.fnHandleActiveDay)
    return;
  
  let activeMonth = ("00").substring(props.activeMonth.toString().length, 2) + props.activeMonth.toString();
  let activeYear = ("0000").substring(props.activeYear.toString().length, 4) + props.activeYear.toString();

  let activeDay = dayjs(`01.${activeMonth}.${activeYear}`,"DD.MM.YYYY")
  if(direction == "Next"){
    activeDay = activeDay.add(1,"month");
  }else{
    activeDay = activeDay.add(-1,"month");
  }
  activeMonth = activeDay.format("MM");
  activeYear = activeDay.format("YYYY");
  props.onRowSelection && props.onRowSelection([]);
  props.fnHandleActiveDay(parseInt(activeMonth),parseInt(activeYear))
  setSurroundingMonths(activeDay.toDate());
};

  const handleExpandRowRenderer =  (id?: string) => {
    if(props.isLoading || !props.data){
      return;
    }
    const hotelName = _.first((props.data as any).result.find( (x:any)=> x.hotelIds.includes(id)).hotelNames) ?? "";
    const variables = {
        hotelIds: [id] as string[],
        agencies: props.formValues.agencies,
        markets: props.formValues.markets,
        rooms: props.formValues.rooms,
        roomTypes: props.formValues.roomTypes,
        boards: props.formValues.boards
    }
    return <ExpandableStopFreeReportList
    hotelName={hotelName.toString()}
    formValues={props.formValues}
    hotelIds={variables.hotelIds}
    agencies={variables.agencies}
    markets={variables.markets}
    rooms={variables.rooms}
    roomTypes={variables.roomTypes}
    boards={variables.boards}
    activeMonth={props.activeMonth}
    activeYear={props.activeYear}
    reportTypes={props.formValues.reportTypes} />;
  }

  return (
        !props.data ? <></> :
        <>
          <div className="nk-block">
            <div className="card card-bordered h-100">
              <div className="card-inner">
                <div className="row">
                  <div className="col-12">
                    <div className="mobile-responsive-block">
                      <div className="inner">
                        <nav>
                          <ul className="center justify-content-around report-table-header">
                            <li>
                              <Button
                                onClick={() => handleArrowClick("Prev")}
                              >
                                  <FontAwesomeIcon icon={faArrowLeft} />
                              </Button>
                            </li>
                            {
                              dateColList?.map((item: any, index: number) => (
                                <li className={props.activeMonth && props.activeMonth == item.month  && props.activeYear == item.year ? "active" : ""} onClick={() => handleActiveDay(item.month,item.year)}>
                                  <a href="#">
                                    <span className="d-block text-center">{item.monthText}<br />{item.year}</span>
                                  </a>
                                </li>
                              ))
                            }
                            <li>
                              <Button
                                onClick={() => handleArrowClick("Next")}
                              >
                                  <FontAwesomeIcon icon={faArrowRight} />
                              </Button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                  {
                    props.data ?
                    
                    <div className="">
                      <div className="inner bordered-table">
                                <StopSaleReport
                                    data={props.data as any}
                                    isLoading={props.isLoading ?? false}
                                    formValues={props.formValues}
                                    agencies={props.agencies ?? []}
                                    markets={props.markets ?? []}
                                    rooms={props.rooms ?? []}
                                    roomTypes={props.roomTypes ?? []}
                                    boards={props.boards ?? []}
                                    reportTypes={props.reportTypes ?? [] as any}
                                    activeMonth={props.activeMonth}
                                    activeYear={props.activeYear} />
                      </div>
                    </div>
                    
                    : ( props.isLoading ? 
                      <>
                          <Skeleton active />
                          <Skeleton active />
                          <Skeleton active />
                      </> : <></>)
                  }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
  );
};
